import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import FormFieldAddDrawer from './FormFieldAddDrawer';

import NuxPopover from '../../../nux/NuxPopover';

import {
  FORMS_ADD_FIELDS_STEP_4,
  FORMS_ADD_FIELDS_STEP_4_FIELD_ADD_TEXT,
} from '../../../nux/nux.constants';

import BorderlessButton from '../../../common/buttons/BorderlessButton';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';

export default function FormFieldAdd({
  onSave,
  field,
  costcodes,
  costcodeIdMap,
  costcodesLoading,
  onEdit,
  isAdd,
  sectionId,
  sections = [],
  isBoardCards,
  isExternalForm,
  isMultiDivisionForm,
  divisions,
  templateId,
  projectId,
  name,
  showDrawer,
  setShowDrawer,
  canAddDynamicAttribute,
  showParentAttributeCheckbox,
  isTimeCard,
  allowRepeatingSections,
  isV3Form = false,
}) {
  const activeNuxAction = useSelector((state) => state.nux.activeNuxAction);

  const save = useCallback((data = {}) => {
    const isNewDropdownField = !data.id && data.selectedType === 'dropdown';
    const updateField = () => {
      if (data.shouldClose) setShowDrawer(false);
      onSave(data);
    };

    if (isNewDropdownField && isExternalForm) {
      CustomConfirmModal({
        title: 'Warning',
        content: 'Adding this field will expose internal company data to external users. By continuing, you acknowledge and accept this risk. Ontraccr assumes no liability for data disclosed through this configuration.',
        onOk: updateField,
      })
    } else {
      updateField();
    }
  }, [onSave, templateId, isExternalForm]);

  return (
    <div>
      <NuxPopover
        placement="bottom"
        visible={activeNuxAction === FORMS_ADD_FIELDS_STEP_4}
        text={FORMS_ADD_FIELDS_STEP_4_FIELD_ADD_TEXT}
      >
        <BorderlessButton
          title="Add Field"
          iconNode={<PlusOutlined />}
          onClick={() => setShowDrawer(true)}
          style={{ width: 100, fontSize: 12, backgroundColor: 'transparent' }}
        />
        <FormFieldAddDrawer
          isAdd={isAdd}
          sectionId={sectionId}
          field={field}
          costcodes={costcodes}
          costcodeIdMap={costcodeIdMap}
          costcodesLoading={costcodesLoading}
          visible={showDrawer}
          onClose={() => {
            setShowDrawer(false);
            onEdit();
          }}
          onSave={save}
          onEdit={onEdit}
          sections={sections}
          isBoardCards={isBoardCards}
          isExternalForm={isExternalForm}
          isMultiDivisionForm={isMultiDivisionForm}
          divisions={divisions}
          templateId={templateId}
          projectId={projectId}
          name={name}
          canAddDynamicAttribute={canAddDynamicAttribute}
          showParentAttributeCheckbox={showParentAttributeCheckbox}
          isTimeCard={isTimeCard}
          allowRepeatingSections={allowRepeatingSections}
          isV3Form={isV3Form}
        />
      </NuxPopover>
    </div>
  );
}

FormFieldAdd.propTypes = {
  onSave: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  isAdd: PropTypes.bool.isRequired,
  field: PropTypes.shape({}),
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeIdMap: PropTypes.shape({}),
  costcodesLoading: PropTypes.bool,
  sectionId: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  isBoardCards: PropTypes.bool,
  isExternalForm: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.shape({})),
  templateId: PropTypes.string,
  projectId: PropTypes.string,
  name: PropTypes.string,
  showDrawer: PropTypes.bool.isRequired,
  setShowDrawer: PropTypes.func.isRequired,
  canAddDynamicAttribute: PropTypes.bool,
  showParentAttributeCheckbox: PropTypes.bool,
  isTimeCard: PropTypes.bool,
  allowRepeatingSections: PropTypes.bool,
  isV3Form: PropTypes.bool,
  isMultiDivisionForm: PropTypes.bool,
};

FormFieldAdd.defaultProps = {
  field: undefined,
  costcodes: [],
  costcodeIdMap: {},
  costcodesLoading: false,
  sectionId: null,
  sections: [],
  isBoardCards: false,
  isExternalForm: false,
  divisions: [],
  templateId: null,
  projectId: null,
  name: null,
  canAddDynamicAttribute: false,
  showParentAttributeCheckbox: false,
  isTimeCard: false,
  allowRepeatingSections: false,
  isV3Form: false,
  isMultiDivisionForm: false,
};
